/**
 * AdminFormsUpdateMigration — TEMPORARY admin-only control.
 *
 * One-off tool (2026-09-02) to UPDATE the 26 previously-migrated Forms
 * Library templates in Production with their latest Preview edits.
 * Matches by exact title only (see backend/forms.py::
 * admin_update_forms_from_preview for the full rationale/safeguards).
 * Dry-run first, real run only after review.
 *
 * Safe to delete this file + its route/AdminHome card, the backend
 * endpoint and data/forms_migration_source_preview_2026-08-30.json once
 * both migrations have been run and confirmed in Production.
 */
import { useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { PageHero } from "../../components/shared/Primitives";

function Row({ items, empty, showChanged, showReason }) {
  if (!items || items.length === 0) return <p className="text-sm text-ink-soft">{empty}</p>;
  return (
    <ul className="mt-1 space-y-1.5 text-sm text-ink">
      {items.map((it, i) => (
        <li key={i}>
          <span className="font-medium">{it.title}</span>
          {showChanged && it.changed_fields && (
            <span className="text-ink-soft"> — changed: {it.changed_fields.join(", ")}</span>
          )}
          {showReason && it.reason && <span className="text-amber-700"> — {it.reason}</span>}
        </li>
      ))}
    </ul>
  );
}

export default function AdminFormsUpdateMigration() {
  const [dryRunResult, setDryRunResult] = useState(null);
  const [realResult, setRealResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirmingReal, setConfirmingReal] = useState(false);
  const [error, setError] = useState("");

  const runDryRun = async () => {
    setLoading(true);
    setError("");
    setRealResult(null);
    try {
      const { data } = await api.post("/api/admin/forms/update-from-preview?dry_run=true");
      setDryRunResult(data);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const runReal = async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.post("/api/admin/forms/update-from-preview?dry_run=false");
      setRealResult(data);
      setConfirmingReal(false);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="admin-forms-update-migration-page">
      <PageHero
        eyebrow="JCC Admin — temporary tool"
        title="Forms: update previously-migrated templates"
        description="One-off tool to sync the 26 already-migrated Forms Library templates with their latest Preview edits. Matches by exact title only, never creates/deletes, flags rather than overwrites anything edited more recently in Production. Run the dry run first."
      />
      <section className="section pt-8">
        <div className="container-jcc max-w-3xl space-y-6">
          {error && (
            <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700" data-testid="admin-forms-update-error">
              {error}
            </div>
          )}

          <button
            type="button"
            onClick={runDryRun}
            disabled={loading}
            className="btn-outline"
            data-testid="admin-forms-update-dry-run-btn"
          >
            {loading ? "Running dry run…" : "Run dry-run comparison"}
          </button>

          {dryRunResult && (
            <div className="card-base p-6 space-y-4" data-testid="admin-forms-update-dry-run-result">
              <p className="text-sm text-ink-muted">Source: {dryRunResult.source_total} templates in the migration bundle.</p>
              <div>
                <p className="font-semibold text-ink" data-testid="admin-forms-update-would-update-count">
                  Would update: {dryRunResult.would_update_count}
                </p>
                <Row items={dryRunResult.would_update} empty="None." showChanged />
              </div>
              <div>
                <p className="font-semibold text-ink" data-testid="admin-forms-update-unchanged-count">
                  Unchanged: {dryRunResult.unchanged_count}
                </p>
                <Row items={dryRunResult.unchanged} empty="None." />
              </div>
              <div>
                <p className="font-semibold text-amber-700" data-testid="admin-forms-update-flagged-count">
                  Flagged (not touched): {dryRunResult.flagged_count}
                </p>
                <Row items={dryRunResult.flagged} empty="None." showReason />
              </div>

              {!realResult && !confirmingReal && dryRunResult.would_update_count > 0 && (
                <button
                  type="button"
                  onClick={() => setConfirmingReal(true)}
                  className="btn-purple"
                  data-testid="admin-forms-update-run-real-btn"
                >
                  Run update
                </button>
              )}

              {confirmingReal && !realResult && (
                <div className="rounded-xl border border-purple/30 bg-purple/5 p-4 space-y-3" data-testid="admin-forms-update-confirm-box">
                  <p className="text-sm text-ink">
                    This will update {dryRunResult.would_update_count} existing Production template(s) in place
                    with the changed fields listed above. Nothing is created or deleted; flagged items are skipped.
                  </p>
                  <div className="flex gap-3">
                    <button type="button" onClick={runReal} disabled={loading} className="btn-purple" data-testid="admin-forms-update-confirm-btn">
                      {loading ? "Running…" : "Yes, run the real update"}
                    </button>
                    <button type="button" onClick={() => setConfirmingReal(false)} className="btn-outline" data-testid="admin-forms-update-cancel-btn">
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {realResult && (
            <div className="card-base p-6 space-y-3" data-testid="admin-forms-update-real-result">
              <p className="font-semibold text-teal" data-testid="admin-forms-update-updated-count">
                Updated: {realResult.updated_count}
              </p>
              <Row items={realResult.updated} empty="None." showChanged />
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
